import { CommissionModule, CommissionCalculationResult, CommissionConfig } from '@/types/commission';
import { COMMISSION_CONFIGS } from '@/data/commission-configs';

/**
 * CommissionManager
 * Scalable service for calculating commissions across different modules
 */
export class CommissionManager {
    /**
     * Get commission configuration for a module
     */
    static getConfig(moduleId: CommissionModule): CommissionConfig {
        const config = COMMISSION_CONFIGS[moduleId];
        if (!config) {
            throw new Error(`Commission configuration not found for module: ${moduleId}`);
        }
        return config;
    }

    /**
     * Calculate commission for a given base price and module
     * @param basePrice - Original price from the provider
     * @param moduleId - The module requesting the calculation
     * @returns CommissionCalculationResult
     */
    static calculate(basePrice: number, moduleId: CommissionModule): CommissionCalculationResult {
        const config = this.getConfig(moduleId);

        if (!config.isActive) {
            return {
                basePrice,
                providerFee: 0,
                guestFee: 0,
                totalGuestPrice: basePrice,
                providerRevenue: basePrice,
                moduleId,
                config,
            };
        }

        // Calculate fees
        const providerFee = (basePrice * config.providerFeePercentage) / 100;
        const guestFee = (basePrice * config.guestFeePercentage) / 100;

        // Calculate final prices
        const totalGuestPrice = basePrice + guestFee;
        const providerRevenue = basePrice - providerFee;

        return {
            basePrice,
            providerFee,
            guestFee,
            totalGuestPrice,
            providerRevenue,
            moduleId,
            config,
        };
    }

    /**
     * Helper to format the result for display
     */
    static formatResult(result: CommissionCalculationResult) {
        return {
            ...result,
            formattedBasePrice: `Rp ${result.basePrice.toLocaleString('id-ID')}`,
            formattedProviderFee: `Rp ${result.providerFee.toLocaleString('id-ID')}`,
            formattedGuestFee: `Rp ${result.guestFee.toLocaleString('id-ID')}`,
            formattedTotalGuestPrice: `Rp ${result.totalGuestPrice.toLocaleString('id-ID')}`,
            formattedProviderRevenue: `Rp ${result.providerRevenue.toLocaleString('id-ID')}`,
        };
    }
}
